import { FocusEventHandler, RefObject, useCallback, useEffect, useState } from 'react';

export default function useFocusBlurEvents(
  orderSelectorRef: RefObject<HTMLElement>,
  onFocus: FocusEventHandler<HTMLElement | undefined>,
  onBlur: FocusEventHandler<HTMLElement | undefined>,
) {
  let [isSelectorOpenForFirstTime, setIsSelectorOpenForFirstTime] = useState<boolean>(true);

  const [isSelectorOpen, setIsSelectorOpen] = useState<boolean>(false);
  const closeOrderSelector = useCallback(() => setIsSelectorOpen(false), []);
  const closeOrderSelectorAndFocus = useCallback(() => {
    closeOrderSelector();
    focusInput();
  }, []);
  const abandonOrderSelector = useCallback(() => {
    closeOrderSelector();
    setIsSelectorOpenForFirstTime(true);
  }, []);
  const openOrderSelector = useCallback(() => setIsSelectorOpen(true), []);
  const openOrderSelectorAndFocus = useCallback(() => {
    openOrderSelector();
    focusInput();
  }, []);
  const toggleOrderSelectorAndFocus = useCallback(() => {
    setIsSelectorOpen((prevState) => !prevState);
    focusInput();
  }, []);

  useEffect(() => {
    if (isSelectorOpen) {
      setIsSelectorOpenForFirstTime(false);
    }
  }, [isSelectorOpen]);

  let onInputFocused = useCallback(
    (e: any) => {
      if (isSelectorOpenForFirstTime) {
        openOrderSelector();
      }
      onFocus && onFocus(e);
    },
    [isSelectorOpenForFirstTime, openOrderSelectorAndFocus, onFocus],
  );
  let focusInput = useCallback(() => {
    orderSelectorRef.current?.querySelector('input')?.focus();
  }, []);

  const [isInteractingWithComponent, setIsInteractingWithComponent] = useState(false);

  const blurIfNotInsideComponent = useCallback(
    (e: any) => {
      if (isInteractingWithComponent) {
        e.preventDefault();
      } else {
        onBlur && onBlur(e);
        abandonOrderSelector();
      }
    },
    [isInteractingWithComponent, focusInput, onBlur, abandonOrderSelector],
  );

  const onInteractionStart = useCallback(() => {
    setIsInteractingWithComponent(true);
    setTimeout(() => setIsInteractingWithComponent(false), 0);
  }, []);

  return {
    onFocusWrapped: onInputFocused,
    onBlurWrapped: blurIfNotInsideComponent,
    onInteractionStart,
    openOrderSelector,
    openOrderSelectorAndFocus,
    closeOrderSelector,
    closeOrderSelectorAndFocus,
    abandonOrderSelector,
    toggleOrderSelectorAndFocus,
    isSelectorOpen,
  };
}
